"use client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import Image from "next/image";
import { ReactNode, useState } from "react";
import aboutus from "@/public/images/aboutus.jpg";
import logo from "@/public/Favicon.svg";
import star from "@/public/images/starBlack.svg";
import time from "@/public/images/whatsapp.svg";
import user from "@/public/images/user.svg";
import trueCircle from "@/public/images/trueCircle.svg";
import { Button } from "@/components/ui/button";
// import LoginFirst from "@/components/shared/LoginFirst";
// import BookingDate from "@/components/shared/BookingDate";
import { DialogClose } from "@radix-ui/react-dialog";
import FreeConsultationDate from "./FreeConsultationDate";
import { useTranslate } from "@/components/base/providers/TranslateProv";

interface Props {
  children: ReactNode;
}

const FreeConsultation = ({ children }: Props) => {
  // const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // let isLoggedIn = true;

  const { locale }: { locale: "ar" | "en" } = useTranslate();

  const translations = {
    en: {
      title: "Book a Consultation with 'University Ambassador'",
      description:
        "Specify your needs for the consultation as the price is determined based on that.",
      evaluation: "Consultation Rating",
      rating: "+5/5 Stars",
      communicationMethod: "Communication Method",
      whatsappOnly: "Messages only via WhatsApp",
      numberOfRequests: "Number of Consultation Requests",
      requestsCount: "+5000 People",
      overviewTitle: "Here's a brief overview of what you can inquire about:",
      overviewItems: [
        "Introduction to all private, national, and special universities in Egypt",
        "Assistance in choosing the right university for you",
        "How to apply to various universities",
        "Assistance in preparing the required documents for application",
        "The consultation will be via messages 'WhatsApp' only",
      ],
      freeConsultation: "Free",
    },
    ar: {
      title: "احجز استشاره مع “سفير الجامعات”",
      description:
        "حدد الحاجات الـ محتجها في الاستشاره الخاصه بيك لان بناء عليها بيتم تحديد السعر",
      evaluation: "تقيم الاستشارة",
      rating: "+5/5 نجوم",
      communicationMethod: "طريقه التواصل",
      whatsappOnly: "رسالة فقط عبر الواتساب",
      numberOfRequests: "عدد طلابات الاستشارة",
      requestsCount: "+5000 شخص",
      overviewTitle: "إليك نبذة عن الأمور التي يمكنك الاستفسار عنها:",
      overviewItems: [
        "التعريف بكافة الجامعات الخاصة والآهلية وذات طابع الخاص في مصر",
        "المساعدة في اختيار الجامعة المناسبة لك",
        "كيفية التقديم بالجامعات المختلفة",
        "المساعدة في تجهيز المستندات المطلوبة في التقديم",
        "الاستشارة ستكون عبر الرسايل “واتساب” فقط",
      ],
      freeConsultation: "مجانا",
    },
  };

  const t = translations[locale];

  return (
    <>
      <Dialog>
        <DialogTrigger asChild className="w-full">{children}</DialogTrigger>
        <DialogContent className="overflow-y-auto overflow-x-hidden max-h-screen">
          <DialogHeader>
            <div className="relative mx-auto">
              <Image
                src={aboutus}
                alt="aboutus"
                className="rounded-full w-28 h-28"
              />
              <Badge className="absolute -top-4 -right-[8px] bg-transparent">
                <Image src={logo} alt="logo" className="w-9" />
              </Badge>
            </div>
            <DialogDescription asChild>
              <div className="my-2">
                <DialogTitle className="text-darkText text-center font-semibold text-[18px] leading-[150%] w-full">
                  {t.title}
                </DialogTitle>
                <p className="text-darkText text-center font-medium text-[14px] leading-[200%] mt-2">
                  {t.description}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 items-center justify-center">
                  <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image src={star} alt="calendar" />
                    <p className="text-13 text-darkText text-center">{t.evaluation}</p>
                    <p className="text-13 text-[#ACB5BB] font-[600] text-center">
                      {t.rating}
                    </p>
                  </div>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image src={time} alt="time" />
                    <p className="text-13 text-darkText text-center">
                      {t.communicationMethod}
                    </p>
                    <p className="text-12 text-[#FF4C51] font-[600] text-center">
                      {t.whatsappOnly}
                    </p>
                  </div>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-2 flex flex-col items-center justify-center gap-3">
                    <Image src={user} alt="calendar" />
                    <p className="text-13 text-darkText text-center">
                      {t.numberOfRequests}
                    </p>
                    <p className="text-13 text-[#ACB5BB] font-[600] text-center">
                      {t.requestsCount}
                    </p>
                  </div>
                </div>
                <div className="border border-gray-200 rounded p-2 mt-4">
                  <h3 className="text-darkText text-center font-medium text-10 leading-normal mb-2 mx-2 border-b pb-2">
                    {t.overviewTitle}
                  </h3>
                  <ul className="m-2 border border-[#EBEBEB] bg-white rounded-lg shadow-lg p-2">
                    {t.overviewItems.map((item, index) => (
                      <li key={index} className="flex items-center my-2">
                        <Image
                          src={trueCircle}
                          alt="true circle"
                          className="ml-2"
                        />
                        <p className="text-darkText text-[13px]">{item}</p>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex mt-4">
                  <div className="w-full">
                    <FreeConsultationDate />
                  </div>
                  <div className="w-fit relative">
                    <Button className="w-fit bg-[#F8F9FA] mr-2 font-semibold text-primaryColor">
                      {t.freeConsultation}
                    </Button>
                    <Badge className="absolute -top-4 -left-[8px] bg-destructive rounded-full">
                      100%
                    </Badge>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FreeConsultation;
